import { Component, DestroyRef, OnInit, PLATFORM_ID, inject, input, output, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { timeout } from 'rxjs';

export interface WorkspaceInvitation {
  id: string;
  organizationId: string;
  organizationName: string;
  propertyName?: string | null;
  unitName?: string | null;
  expiresAt?: string | null;
  role?: string | null;
  ownerClientId?: string | null;
  ownerName?: string | null;
  portalSlug?: string | null;
}

@Component({
  selector: 'apex-workspace-invitations',
  imports: [],
  template: `
    @if (invitations().length || error()) {
      <section class="invitations" aria-label="Pending invitations">
        <h2>{{ invitations().length ? 'Pending invitations' : 'Invitations unavailable' }}</h2>
        @if (invitations().length) { <p>Review invitations from your property team. Accepting gives you the access assigned in the invitation.</p> }
        @if (loading()) { <p role="status">Loading invitations…</p> }
        @if (error()) {
          <p role="alert">{{ error() }}</p>
          <button type="button" (click)="load()" [disabled]="loading() || !!busy()">Refresh invitations</button>
        }
        @for (invitation of invitations(); track invitation.id) {
          <article>
            <div>
              <h3>{{ invitation.organizationName }}</h3>
              <p>{{ invitation.ownerName || roleLabel(invitation.role) }}</p>
              @if (invitation.expiresAt) { <small>Expires {{ expiryLabel(invitation.expiresAt) }}</small> }
            </div>
            <div class="actions">
              <button type="button" class="accept" [disabled]="!!busy()" (click)="respond(invitation, 'accept')">{{ busy() === invitation.id ? 'Updating…' : 'Accept invitation' }}</button>
              <button type="button" [disabled]="!!busy()" (click)="declining.set(invitation.id)">Decline</button>
            </div>
            @if (declining() === invitation.id) {
              <div class="decision" role="group" aria-label="Confirm decline">
                <p>Decline this invitation? Your existing workspace access will stay available.</p>
                <button type="button" [disabled]="!!busy()" (click)="respond(invitation, 'reject')">Confirm decline</button>
                <button type="button" [disabled]="!!busy()" (click)="declining.set(null)">Keep invitation</button>
              </div>
            }
          </article>
        }
      </section>
    }
  `,
  styles: `
    :host { display: block; }
    .invitations { margin-bottom: 1.5rem; padding: 1.5rem; border: 1px solid #dbe3ee; border-radius: 14px; background: #fff; color: #111827; }
    h2, h3 { margin: 0 0 .5rem; font-weight: 600; } h2 { font-size: 1.2rem; } h3 { font-size: 1rem; } p { margin: .5rem 0; } small { color: #64748b; }
    article { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 1rem; border-top: 1px solid #dbe3ee; padding-top: 1rem; margin-top: 1rem; }
    .actions { display: flex; align-items: center; flex-wrap: wrap; gap: .75rem; }
    .decision { flex-basis: 100%; } .decision button { margin-right: .75rem; }
    button { border: 1px solid #64748b; background: transparent; color: inherit; border-radius: 8px; padding: .65rem 1rem; cursor: pointer; font: inherit; }
    button.accept { background: #0e7490; color: #fff; border-color: #0e7490; }
    button:disabled { opacity: .6; cursor: wait; } button:focus-visible { outline: 2px solid #38bdf8; outline-offset: 3px; }
    :host-context(.dark) .invitations, :host-context([data-theme='dark']) .invitations { background: #101827; color: #f1f5f9; border-color: #334155; }
    :host-context(.dark) article, :host-context([data-theme='dark']) article { border-color: #334155; }
    :host-context(.dark) small, :host-context([data-theme='dark']) small { color: #cbd5e1; }
  `,
})
export class WorkspaceInvitations implements OnInit {
  expiryLabel(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    try {
      return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: this.timeZone() || undefined }).format(date);
    } catch {
      return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(date);
    }
  }

  readonly pendingCount = output<number>();
  readonly gatewayUrl = input.required<string>();
  readonly kind = input.required<'owner' | 'staff' | 'maintenance'>();
  readonly portalUrls = input.required<{ backofficeAppUrl: string; ownerAppUrl?: string; maintenanceAppUrl: string }>();
  readonly accepted = output<WorkspaceInvitation>();
  /** IANA zone for the expiry date (the viewer's profile zone). Defaults to the browser zone. */
  readonly timeZone = input<string | null>(null);
  readonly invitations = signal<WorkspaceInvitation[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly busy = signal<string | null>(null);
  readonly declining = signal<string | null>(null);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private get base(): string { return `${this.gatewayUrl()}/property-service/api/v1/me/${this.kind() === 'owner' ? 'owner' : 'staff'}-invitations`; }

  roleLabel(role?: string | null): string { return role ? role.toLowerCase().replaceAll('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Property owner'; }

  private readonly platformId = inject(PLATFORM_ID);

  ngOnInit(): void { if (isPlatformBrowser(this.platformId)) this.load(); }

  load(): void {
    if (!isPlatformBrowser(this.platformId) || this.loading()) return;
    this.loading.set(true);
    this.error.set(null);
    this.http.get<WorkspaceInvitation[]>(this.base, { withCredentials: true })
      .pipe(timeout(12000), takeUntilDestroyed(this.destroyRef)).subscribe({
        next: rows => {
          const visible = this.kind() === 'maintenance' ? rows.filter(row => maintenanceInvitationRoles.includes(row.role || '')) : rows;
          this.invitations.set(visible); this.pendingCount.emit(visible.length); this.loading.set(false);
        },
        error: () => { this.error.set('Invitations could not be loaded. Please try again.'); this.loading.set(false); },
      });
  }

  protected openWorkspace(invitation: WorkspaceInvitation): void {
    window.location.assign(invitationWorkspaceUrl(invitation, this.kind(), this.portalUrls()));
  }

  respond(invitation: WorkspaceInvitation, decision: 'accept' | 'reject'): void {
    if (this.busy()) return;
    this.busy.set(invitation.id);
    this.error.set(null);
    this.http.post(`${this.base}/${encodeURIComponent(invitation.id)}/${decision}`, {}, { withCredentials: true })
      .pipe(timeout(12000), takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.busy.set(null);
          this.declining.set(null);
          this.invitations.update(rows => rows.filter(row => row.id !== invitation.id));
          this.pendingCount.emit(this.invitations().length);
          if (decision === 'accept') {
            this.accepted.emit(invitation);
            this.openWorkspace(invitation);
          }
        },
        error: () => {
          this.busy.set(null);
          this.error.set('The invitation could not be updated. It may have expired or changed. Refresh invitations and try again.');
        },
      });
  }
}


export const maintenanceInvitationRoles = ['MAINTENANCE_MANAGER', 'MAINTENANCE_COORDINATOR', 'MAINTENANCE_STAFF'];

export function invitationWorkspaceUrl(invitation: WorkspaceInvitation, kind: 'owner' | 'staff' | 'maintenance', urls: {
  backofficeAppUrl: string; ownerAppUrl?: string; maintenanceAppUrl: string;
}): string {
  if (kind === 'owner' && !urls.ownerAppUrl) throw new Error('Owner portal URL is required.');
  const maintenance = kind === 'maintenance' || ['MAINTENANCE_STAFF'].includes(invitation.role || '');
  const url = new URL('/dashboard', kind === 'owner' ? urls.ownerAppUrl! : maintenance ? urls.maintenanceAppUrl : urls.backofficeAppUrl);
  const slug = invitation.portalSlug;
  if (kind !== 'maintenance' && !maintenance && slug && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)
      && !['localhost', '127.0.0.1', '[::1]'].includes(url.hostname) && !url.hostname.endsWith('.localhost')) {
    if (!url.hostname.startsWith(`${slug}.`)) url.hostname = `${slug}.${url.hostname}`;
  }
  url.searchParams.set('orgId', invitation.organizationId);
  if (kind === 'owner' && invitation.ownerClientId) url.searchParams.set('ownerEntityId', invitation.ownerClientId);
  return url.toString();
}
